<script setup lang="ts">
/**
 * The one-time nudge into basic training, for an account that has done nothing.
 *
 * Whether it is shown at all is `garage/training-prompt.ts` — a pure decision
 * over two facts the garage overview already carries — and the reasoning for both
 * halves lives there. This file is the dialog.
 *
 * ── Why a modal, when this file's own rules are suspicious of them ────────────
 *
 * The garage is deliberately a place rather than a queue of prompts, and a
 * control that advertises something it cannot deliver is the failure this
 * codebase names most often. Both point the same way here:
 *
 * - It **can** deliver. The button starts the tutorial through the same path the
 *   picker's own card uses, so there is nothing to promise.
 * - It is unmissable exactly once, and then never again — a panel floating in a
 *   corner of a garage the player has never seen before is a panel they will not
 *   read, and the corners are all spoken for besides.
 * - The player has nothing else to be doing. A modal is rude when it interrupts;
 *   this is the first frame of the session for somebody who has not driven a tank
 *   yet, so there is no task to interrupt.
 *
 * ── Three properties are load-bearing ────────────────────────────────────────
 *
 * **It cannot trap anybody.** Escape, the backdrop, the close button and *Not
 * now* all resolve to the same dismissal, so every ordinary way of getting out of
 * a dialog works. That matters more here than in any other modal in the game,
 * because the audience is a player who has not learned this UI yet.
 *
 * **It states what training costs, which is nothing.** The tutorial is the one
 * join that charges no fuel and banks no credits, and a new player has no reason
 * to assume that — so the line says it, and it is the honest inverse of the fuel
 * cost the In-battle button states for every other sortie.
 *
 * **It says what the drills are, briefly.** A CTA that says only "try the
 * tutorial" is asking for trust; naming the six things it teaches is the same
 * argument the orders panel makes for listing all six keys rather than the
 * standing one — a player who does not already know a feature exists has nowhere
 * else to learn it.
 */
import { onMounted, ref } from 'vue';

const emit = defineEmits<{
  /** Start basic training. The screen owns the launch, exactly as it does deploy. */
  start: [];
  /** *Not now* — and the screen remembers it for this player. */
  dismiss: [];
}>();

const dialog = ref<HTMLElement | null>(null);
onMounted(() => dialog.value?.focus());

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('dismiss');
  }
}
</script>

<template>
  <div class="scrim" data-test="training-prompt" @click.self="emit('dismiss')">
    <section
      ref="dialog"
      class="window"
      role="dialog"
      aria-modal="true"
      aria-labelledby="training-prompt-title"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header>
        <p class="eyebrow">New crew</p>
        <h2 id="training-prompt-title">Run basic training first</h2>
      </header>

      <p class="lead">
        Six drills in a private yard: look around, drive, fire, take out one tank,
        collect a supply crate, then clear the yard with your section beside you.
      </p>

      <ul class="drills">
        <li>Real tanks, the real controls, the real server.</li>
        <li>Costs no fuel and pays nothing — nothing to lose by trying it.</li>
        <li>You cannot fail it. Get destroyed and you are put straight back.</li>
      </ul>

      <footer>
        <button type="button" class="ghost" data-test="training-later" @click="emit('dismiss')">
          Not now
        </button>
        <button type="button" class="go" data-test="training-start" @click="emit('start')">
          Start basic training
        </button>
      </footer>
    </section>
  </div>
</template>

<style scoped>
/* The shell is `ConvertCreditsModal`'s and `RankLadder`'s, copied rather than
   reinvented: a scrim that dismisses on backdrop click, a window that takes
   focus on mount and closes on Escape. */
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(6, 8, 11, 0.72);
  z-index: 40;
}

.window {
  width: min(30rem, 100%);
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.2rem 1.3rem 1.3rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  /* One accent edge and nothing louder. The hazard striping is spent on the
     landing page's closing call and on a payout ribbon; a third helping would
     make it wallpaper. */
  border-top: 2px solid var(--accent);
  border-radius: 0.5rem;
  background: #12151a;
}

.eyebrow {
  margin: 0 0 0.15rem;
  color: var(--accent);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 1.35rem;
}

.lead {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.5;
  color: var(--steel-200);
}

.drills {
  margin: 0;
  padding-left: 1.05rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.78rem;
  line-height: 1.45;
  /* `steel-200`, not `steel-400` — on this dialog's solid plate `steel-400` reads
     at 3.83:1, under the 4.5:1 this text size needs. */
  color: var(--steel-200);
}

footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  /* Wraps rather than shrinking the primary button below its label: at 320 px
     the two do not fit on one line, and a clipped call to action is worse than
     a stacked pair. */
  flex-wrap: wrap;
  margin-top: 0.2rem;
}

footer button {
  padding: 0.55rem 1rem;
  border-radius: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ghost {
  border: 1px solid var(--steel-600);
  background: none;
  /* `steel-200`, not `steel-400` — see `.drills`; this is the only way out of the
     dialog besides Escape and the backdrop, so it has to read clearly at rest. */
  color: var(--steel-200);
}

.ghost:hover {
  border-color: var(--steel-400);
  color: var(--steel-200);
}

.go {
  border: 1px solid var(--accent);
  background: var(--accent);
  color: #12151a;
}

.go:hover {
  background: #f0834a;
}
</style>
